import { FC } from "react";
import {
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { DisbursementVisit } from "../../../../../services/reports.service";
import { resolveDisbursementResidual } from "../../../../../utils/disbursementResidual";
import { ResultPill } from "../ResultPill";
import { useAppTheme } from "../../../../../utils/theme";

/**
 * A value the record genuinely does not carry.
 *
 * Distinct from SUPPRESSED_NAME's em dash on the row above, which means "this
 * account withholds it" — here it means "this kind of activity has no such
 * field". Rendering "" instead made a legitimate row look broken.
 */
const ABSENT = "—";

interface Props {
	id: string;
	visits: DisbursementVisit[];
	earnedTotal: number;
	/** PRD DR-8: the sub-footer carries what was PAID, not what was earned. */
	disbursedAmount: number;
	currencySymbol: string;
	isLoading: boolean;
	error: string | null;
	colors: {
		border: string;
		divider: string;
		band: string;
		muted: string;
		ink: string;
		error: string;
	};
	formatDate: (value: string) => string;
}

/**
 * The visits behind one disbursement — SIMS-8131 (PRD DR-7).
 *
 * Deliberate deviations from the Hi-Fi, each tied to the rule that forces it:
 *
 * - **Real table semantics**, not the drawing's grid of `<div>`s (WCAG 1.3.1) —
 *   the same call SIMS-8120 made for the table this panel sits inside.
 * - **The result is not conveyed by colour alone** (WCAG 1.4.1). The drawing
 *   carries the whole meaning in the pill's fill, and inverts it besides
 *   (Negative green, Positive red — domain-correct, but unreadable to anyone
 *   who cannot see the colour). The label text is always present, and the
 *   colour reinforces it rather than carrying it.
 * - **The chip itself is shared with Visit Detail** (`../ResultPill`, SIMS-9012).
 *   This panel used to render one neutral pill for every result while the
 *   report one click away coloured them, so the same visit looked like two
 *   different things. The fill is the configured `EventResult.displayColor`,
 *   never derived from the label.
 * - **Amounts use the symbol the response carried** (AC3), never a literal `$`.
 *
 * The panel renders no participant name: the drill-down answers "what did this
 * payout cover?", and the row above already identifies who it belongs to.
 */
const DisbursementVisitsPanel: FC<Props> = ({
	id,
	visits,
	earnedTotal,
	disbursedAmount,
	currencySymbol,
	isLoading,
	error,
	colors: c,
	formatDate,
}) => {
	// SIMS-9012: the shared chip resolves its fallback palette from the theme,
	// the same way Visit Detail does.
	const theme = useAppTheme();

	const money = (n: number): string =>
		`${currencySymbol}${(n ?? 0).toFixed(2)}`;

	// SIMS-8138: null when the payout reconciles with its visits (AC3).
	const residual = resolveDisbursementResidual(disbursedAmount, earnedTotal);

	const shell = (children: React.ReactNode): JSX.Element => (
		<Box
			id={id}
			sx={{
				bgcolor: c.band,
				borderBottom: `1px solid ${c.border}`,
				borderLeft: "3px solid",
				borderLeftColor: "primary.main",
				p: 2,
				pl: 5.5,
			}}
		>
			{children}
		</Box>
	);

	if (isLoading) {
		return shell(
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
				<CircularProgress size={18} aria-label="Loading visits" />
				<Typography variant="body2" sx={{ color: c.muted }}>
					Loading visits…
				</Typography>
			</Box>,
		);
	}

	if (error) {
		// Surfaced in the panel rather than swallowed: a drill-down that silently
		// shows nothing is indistinguishable from AC2's "no linked visits", and
		// those two mean opposite things to someone reconciling a payout.
		return shell(
			<Typography role="alert" variant="body2" sx={{ color: c.error, p: 2 }}>
				{error}
			</Typography>,
		);
	}

	// AC2 — a real payout with nothing linked. Not an error, and not empty space.
	if (visits.length === 0) {
		return shell(
			<Box
				sx={{
					bgcolor: "background.paper",
					border: `1px solid ${c.border}`,
					borderRadius: 2,
					p: 2.5,
				}}
			>
				<Typography variant="body2" sx={{ color: c.muted }}>
					No linked visits.
				</Typography>
			</Box>,
		);
	}

	return shell(
		<Box
			sx={{
				bgcolor: "background.paper",
				border: `1px solid ${c.border}`,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					p: 1.5,
					borderBottom: `1px solid ${c.divider}`,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					gap: 2,
				}}
			>
				<Typography sx={{ fontWeight: 600, fontSize: 14 }}>
					Visits included in this disbursement
				</Typography>
				<Typography variant="caption" sx={{ color: c.muted }}>
					{visits.length} {visits.length === 1 ? "visit" : "visits"} · earned{" "}
					{money(earnedTotal)}
				</Typography>
			</Box>

			<TableContainer sx={{ overflowX: "auto" }}>
				<Table size="small" aria-label="Visits included in this disbursement">
					<TableHead>
						<TableRow sx={{ bgcolor: c.band }}>
							<TableCell>Visit Date</TableCell>
							<TableCell>Visit Number</TableCell>
							{/*
							 * SIMS-8131 review finding. Not every linked entry is a
							 * VISIT: medication proofs (PHOTOS/COMPOUND) and check-ins
							 * (FORM) earn against a payout but carry no visitDate,
							 * visitNumber, provider or result. Without the activity
							 * name such a row rendered as an amount beside five empty
							 * cells and read as broken.
							 *
							 * They are NOT filtered out — they are real earned money,
							 * and dropping them would make this panel disagree with the
							 * payout and hand SIMS-8138 a residual that is not there.
							 */}
							<TableCell>Activity</TableCell>
							<TableCell>CM Coordinator</TableCell>
							<TableCell align="right">Visit Earned Amount</TableCell>
							<TableCell>Visit Result</TableCell>
							<TableCell>Visit Payer Plan</TableCell>
							{/* SIMS-9294: the plan's member ID, beside the plan it belongs to. */}
							<TableCell>Visit Payer Plan ID</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visits.map((v, i) => (
							<TableRow
								key={`${v.visitDate}-${v.visitNumber ?? i}-${v.earnedAmount}`}
							>
								<TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
									{v.visitDate ? formatDate(v.visitDate) : ABSENT}
								</TableCell>
								<TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
									{/* An em dash, not "0" — a visit zero would be a real number. */}
									{v.visitNumber ?? ABSENT}
								</TableCell>
								<TableCell>{v.activity || ABSENT}</TableCell>
								<TableCell>{v.cmCoordinator || ABSENT}</TableCell>
								<TableCell
									align="right"
									sx={{ fontVariantNumeric: "tabular-nums" }}
								>
									{money(v.earnedAmount)}
								</TableCell>
								<TableCell>
									{/*
									 * SIMS-9012: the SAME chip Visit Detail renders, from
									 * ../ResultPill. This used to be a single neutral pill for
									 * every result, so the identical visit looked like two
									 * different things one click apart.
									 *
									 * The fill is the admin-configured colour when the result
									 * has one; otherwise the shared keyword fallback applies —
									 * which is what runs on this account, since its visits
									 * carry no event to configure a colour on.
									 */}
									<ResultPill
										value={v.result || null}
										color={v.resultColor ?? null}
										theme={theme}
										neutral={{ bg: c.band, color: c.muted }}
										absent={ABSENT}
									/>
								</TableCell>
								<TableCell
									sx={{ color: c.muted }}
									data-testid="visit-payer-plan"
								>
									{v.payerPlan ?? ABSENT}
								</TableCell>
								<TableCell
									sx={{ color: c.muted }}
									data-testid="visit-payer-plan-id"
								>
									{v.payerPlanNumber ?? ABSENT}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						{/*
						 * SIMS-8138 — the reconciliation guardrail (PRD DR-8).
						 *
						 * Placed BETWEEN the visits and the disbursement total, so the
						 * column reads visits → residual → payout and AC1's "makes the
						 * drill-down sum to the disbursed amount" is literally true down
						 * the page. Absent entirely when the payout reconciles (AC3).
						 *
						 * Not waivable (C-3): any difference that is non-zero to the cent
						 * is shown, with no threshold and no flag to suppress it.
						 */}
						{residual && (
							<TableRow>
								<TableCell colSpan={4} sx={{ color: c.ink }}>
									{/* The label carries the direction, so the amount is
									    always positive and never reads as a negative payout. */}
									{residual.label}
								</TableCell>
								<TableCell
									align="right"
									sx={{
										fontWeight: 600,
										color: c.ink,
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{/* AC4: the symbol the response carried, matching every
									    other amount here and the row above. Never a literal. */}
									{money(residual.amount)}
								</TableCell>
								<TableCell colSpan={3} />
							</TableRow>
						)}
						<TableRow sx={{ bgcolor: c.band }}>
							<TableCell colSpan={4} sx={{ color: c.ink }}>
								<strong>Disbursement total</strong>
							</TableCell>
							<TableCell
								align="right"
								sx={{
									fontWeight: 700,
									color: c.ink,
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{/* DR-8: what was PAID. On every example the Hi-Fi draws this
								    equals the visits' sum, so the drawing cannot distinguish
								    them — on the cases the SIMS-8138 guardrail exists for they
								    differ, and the reader needs the payout. */}
								{money(disbursedAmount)}
							</TableCell>
							<TableCell colSpan={3} />
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</Box>,
	);
};

export default DisbursementVisitsPanel;
